/**
 * The Commissioner's house rules (ISSUE-1041 R4): the preamble every rules
 * view already renders above the generated rules, written through
 * `POST /pool/update { commissionerPreamble }`. An emptied field sends null and
 * removes the preamble.
 *
 * Copies `PoolSettingsNameField.tsx`: explicit save, the draft follows the
 * query until the fan types, `SavedNote` carries the outcome, refusals go to
 * the shared error modal.
 */
import { usePoolUpdate } from "@sportsmet/pool-sdk/private";
import { Button, cn, FOCUS_RING, SavedNote } from "@sportsmet/ui";
import { type ReactNode, useEffect, useId, useState } from "react";

import { showApiError, showOfflineError } from "../../errors/apiErrorBus";
import {
  HOUSE_RULES_HINT,
  HOUSE_RULES_LABEL,
  HOUSE_RULES_MAX_LENGTH,
  HOUSE_RULES_SAVE_LABEL,
  resolveHouseRulesFieldState,
} from "./houseRulesFieldState";

export type typePoolSettingsHouseRulesFieldProps = {
  poolId: string;
  /** The saved preamble from `POST /pool/rules-get`. */
  savedPreamble: string | null;
  /** Runs after a save lands, so the rules above re-read the new preamble. */
  onSaved: () => void;
};

export const PoolSettingsHouseRulesField = ({
  poolId,
  savedPreamble,
  onSaved,
}: typePoolSettingsHouseRulesFieldProps): ReactNode => {
  const update = usePoolUpdate();
  const [draft, setDraft] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  // Switching pools discards a draft, as on the name field.
  useEffect(() => {
    setDraft(null);
    setIsSaved(false);
  }, [poolId]);

  const value = draft ?? savedPreamble ?? "";
  const state = resolveHouseRulesFieldState({ draft: value, savedPreamble });

  async function handleSave(): Promise<void> {
    if (!state.isSaveEnabled) return;
    try {
      const result = await update.mutateAsync({
        poolId,
        commissionerPreamble: state.commissionerPreamble,
      });
      if (showApiError(result)) return;
      setDraft(null);
      setIsSaved(true);
      onSaved();
    } catch {
      showOfflineError();
    }
  }

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="pool-settings-house-rules-field"
    >
      <label htmlFor={id} className="text-caption text-ink">
        {HOUSE_RULES_LABEL}
      </label>
      <textarea
        id={id}
        rows={5}
        maxLength={HOUSE_RULES_MAX_LENGTH}
        value={value}
        data-testid="pool-settings-house-rules-input"
        aria-describedby={state.error !== null ? errorId : hintId}
        aria-invalid={state.error !== null || undefined}
        className={cn(
          "min-h-touch-target w-full rounded-sm border bg-surface px-3 py-3 text-input text-ink",
          state.error !== null ? "border-error" : "border-line",
          FOCUS_RING,
        )}
        onChange={(event) => {
          setDraft(event.target.value);
          setIsSaved(false);
        }}
      />
      {state.error !== null ? (
        <p id={errorId} role="alert" className="text-caption text-error">
          {state.error}
        </p>
      ) : (
        <p id={hintId} className="text-caption text-ink-secondary">
          {HOUSE_RULES_HINT}
        </p>
      )}
      <div className="flex items-center gap-3">
        <Button
          variant="secondary"
          size="sm"
          data-testid="pool-settings-house-rules-save"
          disabled={!state.isSaveEnabled || update.isPending}
          onClick={() => {
            void handleSave();
          }}
        >
          {HOUSE_RULES_SAVE_LABEL}
        </Button>
        <div data-testid="pool-settings-house-rules-saved">
          <SavedNote saved={isSaved} />
        </div>
      </div>
    </div>
  );
};
